// @ts-nocheck
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxStyle } from "@insite/mobius/Checkbox";
import { StyledButton } from "@insite/mobius/Clickable/Clickable";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { formSmallGridProps } from "@store/Themes/common/props/form";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

/* Store Custom Code - Start */
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import getColor from "@insite/mobius/utilities/getColor";
import Loading from "@store/Library/Components/Icons/Loading";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
/* Store Custom Code - End */

export const tableGetEllipsisCellProps = (width: number): DataTableCellProps => ({
    css: css`
        > ${StyledButton}, > ${TypographyStyle}, > ${TypographyStyle} > ${StyledButton} {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: block;
            max-width: ${pxToRem(width)};
        }

        > ${StyledButton} {
            display: inline-block;
        }
    `,
});

/* Store Custom Code - Start */
export const tableGetSizedCellProps = (width: number): DataTableCellProps => ({
    css: css`
        min-width: ${pxToRem(width)};
        white-space: normal;
    `,
});
/* Store Custom Code - End */

export const tableRemoveEllipsisCellProps = (): DataTableCellProps => ({
    css: css`
        > ${StyledButton}, > ${TypographyStyle} {
            overflow: visible;
            white-space: inherit;
            max-width: none;
        }
    `,
});

export interface TableProps {
    loadingOverlay?: LoadingOverlayProps;
    headingWrapper?: FlexWrapperPresentationProps;
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    selectionHeader?: DataTableHeaderProps;
    selectionCell?: DataTableCellProps;
    amountHeader?: DataTableHeaderProps;
    amountCell?: DataTableCellProps;
    tightHeader?: DataTableHeaderProps;
    tightCell?: DataTableCellProps;
    button?: ButtonPresentationProps;
    narrowCell?: DataTableCellProps;
    // Store Custom Code
    shipToCell?: DataTableCellProps;
    tightWrapHeader?: DataTableHeaderProps;
}

const selectionStyles = (invert?: boolean) => css`
    text-align: center;
    line-height: 1;

    ${CheckboxStyle} {
        display: flex;

        [role="checkbox"] {
            margin: 0;
            ${invert ? `border-color: transparent !important;` : null}

            + label {
                padding: 0;
            }
        }
    }

    ${RadioStyle} {
        display: flex;

        label {
            font-size: 0;
            color: transparent;

            &::before,
            &::after {
                margin: 0;
            }
        }
    }
`;

export const tableProps: TableProps = {
    loadingOverlay: { minHeight: 150 },
    headingWrapper: {
        gap: pxToRem(16),
        verticalGap: pxToRem(8),
        extendedStyles: {
            wrapper: resultCountProps.wrapper,
            content: {
                css: css`
                    justify-content: space-between;
                `,
            },
        },
    },
    wrapper: {
        css: css`
            position: relative;
            width: 100%;
            overflow-x: auto;
        `,
    },
    selectionHeader: {
        tight: true,
        css: selectionStyles(true),
    },
    selectionCell: {
        tight: true,
        css: selectionStyles(),
    },
    amountHeader: {
        tight: true,
        css: css`
            text-align: left; /* Store Custom Code */

            > ${StyledButton} {
                justify-content: flex-end;
            }
        `,
    },
    amountCell: {
        tight: true,
        css: css`
            text-align: left; /* Store Custom Code */
        `,
    },
    tightHeader: { tight: true },
    tightCell: {
        tight: true,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
        `,
    },
    button: {
        color: "primary",
        sizeVariant: "small",
        /* Store Custom Code - Start */
        mergeCss: true,
        /* Store Custom Code - End */
        css: css`
            /* Store Removed - x.theme.button.primary.css */
            position: relative;
            min-width: 0;
        `,
    },
    narrowCell: {
        css: css`
            padding-top: ${pxToRem(8)};
            padding-bottom: ${pxToRem(8)};
        `,
    },
    /* Store Custom Code - Start */
    shipToCell: {
        css: css`
            ${tableGetEllipsisCellProps(150).css}

            ${breakpoint("xl")} {
                ${tableGetEllipsisCellProps(250).css}
            }
        `,
    },
    /* Store Custom Code - End */
};

export interface TableFilterProps {
    wrapper?: InjectableCss;
    accordion?: AccordionPresentationProps;
    formGridContainer?: GridContainerProps;
    clearButton?: ButtonPresentationProps;
    tagWrapper?: TagWrapperProps;
    // Store Custom Code
    exportLink?: LinkPresentationProps;
    exportLoadingLink?: LinkPresentationProps;
    exportTooltip?: TooltipPresentationProps;
    exportModal?: ModalPresentationProps;
    exportModalText?: TypographyPresentationProps;
}

export const tableFilterProps: TableFilterProps = {
    wrapper: {
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.large)}
        `,
    },
    accordion: {
        mergeCss: true,
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.base)}
        `,
    },
    formGridContainer: formSmallGridProps.gridContainer,
    clearButton: { variant: "tertiary" }, // Store Custom Code
    /* Store Custom Code - Start */
    tagWrapper: {
        extendedStyles: {
            wrapper: {
                css: css`
                    ${spacingStyles(null, pxToRem(20))}
                `,
            },
        },
    },
    exportLink: linkGetActionLinkProps(Download),
    exportLoadingLink: linkGetActionLinkProps(Loading),
    exportTooltip: {
        iconProps: {
            src: InfoFill,
            color: "inherit",
            size: 20,
        },
        mergeCss: true,
        cssOverrides: {
            tooltipContainer: css`
                transform: translateX(-90%);
                width: ${pxToRem(196)};
            `,
            tooltipArrow: css`
                left: 90%;
            `,
            tooltipClickable: css`
                color: ${getColor("text.link")};
                transition: ${transitionHoverStyles("color")};

                &:hover {
                    color: ${getColor("text.linkHover")};
                }
            `,
        },
    },
    exportModal: { size: 500 },
    exportModalText: {
        variant: "p",
        weight: "bold",
    },
    /* Store Custom Code - End */
};
